import { Pressable, StyleProp, StyleSheet, Text, TextStyle, View, ViewStyle, Platform } from 'react-native'
import React, { memo, useState } from 'react'
import Animated, {
  Easing,
  SharedValue,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming
} from 'react-native-reanimated'
import { color } from '../../../theme'

const HORIZONTAL_SPACE = 4
const DEFAULT_VERTICAL_SPACE = 2 * HORIZONTAL_SPACE

const Segment = ({
  value,
  content,
  onPress,
  customSegmentStyle,
  textStyle
}: {
  value?: string
  onPress: () => void
  content?: JSX.Element | string
  customSegmentStyle?: StyleProp<ViewStyle>
  textStyle?: StyleProp<TextStyle>
}) => {
  const renderContent = () => {
    if (typeof content === 'string') {
      return <Text style={[{ color: 'black' }, textStyle]}>{content}</Text>
    }
    return content
  }

  return (
    <Pressable onPress={onPress} style={[styles.segment, customSegmentStyle]}>
      {renderContent()}
    </Pressable>
  )
}

const SelectedSegment = ({
  index,
  segmentsCount,
  selectedSegmentWidth,
  content,
  borderRadius
}: {
  index: SharedValue<number>
  segmentsCount: number
  selectedSegmentWidth: number
  content: JSX.Element
  borderRadius: number
}) => {
  const animatedStyles = useAnimatedStyle(() => {
    return {
      left: interpolate(
        index.value,
        [0, segmentsCount - 1],
        [HORIZONTAL_SPACE, selectedSegmentWidth * (segmentsCount - 1) - HORIZONTAL_SPACE]
      ),
      width: selectedSegmentWidth
    }
  })

  return <Animated.View style={[animatedStyles, styles.selectedSegment, { borderRadius }]}>{content}</Animated.View>
}

const SegmentControl = memo(
  ({
    inactiveSegments,
    activeSegments,
    parentWidth,
    customSegmentStyle,
    initialSelectedIndex = 0,
    onChangeIndex,
    borderRadius = 24
  }: {
    inactiveSegments: Array<{ content: JSX.Element }> | Array<string>
    activeSegments: Array<{ content: JSX.Element }> | Array<string>
    parentWidth: number
    initialSelectedIndex?: number
    customSegmentStyle?: StyleProp<ViewStyle>
    onChangeIndex?: (index: number) => void
    borderRadius?: number
  }) => {
    const selectedIndex = useSharedValue(initialSelectedIndex)
    const segmentsCount = inactiveSegments.length
    const selectedSegmentWidth = parentWidth / segmentsCount
    const [selectedValueIndex, setSelectedValueIndex] = useState(selectedIndex.value)

    // Guard for mismatched arrays after refactors/upgrades
    if (activeSegments.length !== segmentsCount) {
      console.warn('SegmentControl: activeSegments and inactiveSegments length mismatch')
    }

    const selectedSegmentContent = activeSegments[selectedValueIndex]

    return (
      <View style={[styles.container, { width: parentWidth }]}>
        <View style={[styles.segmentsWrapper, { borderRadius }]}>
          <SelectedSegment
            index={selectedIndex}
            segmentsCount={segmentsCount}
            selectedSegmentWidth={selectedSegmentWidth}
            borderRadius={borderRadius}
            content={
              <Segment
                onPress={() => {
                  // noop
                }}
                customSegmentStyle={customSegmentStyle}
                content={
                  typeof selectedSegmentContent === 'string' ? selectedSegmentContent : selectedSegmentContent?.content
                }
                textStyle={styles.selectedSegmentText}
              />
            }
          />
          {inactiveSegments.map((segment, index) => (
            <Segment
              key={`segment-${index}`}
              onPress={() => {
                setSelectedValueIndex(index)
                onChangeIndex?.(index)
                selectedIndex.value = withTiming(index, {
                  duration: 350,
                  easing: Easing.bezier(0.25, 0.1, 0.25, 1)
                })
              }}
              customSegmentStyle={customSegmentStyle}
              content={typeof segment === 'string' ? segment : segment.content}
            />
          ))}
        </View>
      </View>
    )
  }
)

export default SegmentControl

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row'
  },
  segmentsWrapper: {
    flex: 1,
    flexDirection: 'row',
    borderRadius: 24,
    backgroundColor: color.palette.lightGrey,
    padding: HORIZONTAL_SPACE,
    position: 'relative'
  },
  segment: {
    flex: 1,
    paddingVertical: DEFAULT_VERTICAL_SPACE,
    alignItems: 'center',
    justifyContent: 'center'
  },
  selectedSegment: {
    backgroundColor: 'white',
    borderRadius: 24,
    position: 'absolute',
    // Use top & bottom so we don’t need a calc() height on web
    top: HORIZONTAL_SPACE,
    bottom: HORIZONTAL_SPACE,
    zIndex: 1
  },
  selectedSegmentText: {
    color: 'black',
    fontWeight: '600'
  }
})
